/**
 * Structured API Error Handling
 *
 * Provides typed error codes, retry logic, and consistent error parsing
 * for all frontend-to-backend communication.
 */

import { offerUpgrade, UPSELL_CODES } from './upsell';

// ── Error Codes (mirrors backend ErrorCode enum) ────────────────────────────

export const ErrorCode = {
  // Client errors
  INVALID_REQUEST: 'INVALID_REQUEST',
  AUTHENTICATION_FAILED: 'AUTHENTICATION_FAILED',
  AUTHORIZATION_FAILED: 'AUTHORIZATION_FAILED',
  PAYMENT_REQUIRED: 'PAYMENT_REQUIRED',  // 402 — plan cap / paid-feature upsell
  RESOURCE_NOT_FOUND: 'RESOURCE_NOT_FOUND',
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  RATE_LIMIT_EXCEEDED: 'RATE_LIMIT_EXCEEDED',
  INSUFFICIENT_DATA: 'INSUFFICIENT_DATA',
  DUPLICATE_RESOURCE: 'DUPLICATE_RESOURCE',
  // Server errors
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  CALCULATION_FAILED: 'CALCULATION_FAILED',
  DATABASE_ERROR: 'DATABASE_ERROR',
  EXTERNAL_SERVICE_ERROR: 'EXTERNAL_SERVICE_ERROR',
  /** The backend could not build a comparison or analysis; its `error` says why ([[BUGS #369]]). */
  COMPUTE_UNAVAILABLE: 'COMPUTE_UNAVAILABLE',
  TIMEOUT_ERROR: 'TIMEOUT_ERROR',
  // Network (client-side only)
  NETWORK_ERROR: 'NETWORK_ERROR',
  ABORT_ERROR: 'ABORT_ERROR',
} as const;

export type ErrorCodeType = typeof ErrorCode[keyof typeof ErrorCode];

// ── Structured API Error ────────────────────────────────────────────────────

export interface ApiErrorResponse {
  error: string;
  error_code?: ErrorCodeType;
  details?: Record<string, unknown>;
  status_code?: number;
}

export class ApiError extends Error {
  readonly code: ErrorCodeType;
  readonly status: number;
  readonly details: Record<string, unknown>;
  readonly retryable: boolean;

  constructor(message: string, code: ErrorCodeType, status: number, details?: Record<string, unknown>) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details ?? {};
    this.retryable = RETRYABLE_CODES.has(code) || (status >= 500 && status < 600);
  }
}

const RETRYABLE_CODES = new Set<ErrorCodeType>([
  ErrorCode.TIMEOUT_ERROR,
  ErrorCode.EXTERNAL_SERVICE_ERROR,
  ErrorCode.DATABASE_ERROR,
  ErrorCode.NETWORK_ERROR,
]);

// ── Error Parser ────────────────────────────────────────────────────────────

/**
 * Parse a fetch Response into a typed ApiError.
 * Call this when response.ok is false.
 */
export async function parseApiError(response: Response): Promise<ApiError> {
  let body: ApiErrorResponse = { error: `HTTP ${response.status}` };
  try {
    body = await response.json();
  } catch {
    // Response body isn't JSON — use status text
  }

  const code = (body.error_code as ErrorCodeType) ?? statusToCode(response.status);
  const message = body.error || response.statusText || `Request failed (${response.status})`;
  // A paywall is a path, not only a sentence (ROADMAP 0.R R2): the app-wide
  // UpgradeOffer opens the plan that lifts it. The caller still gets the error.
  if (response.status === 402) offerUpgrade(String(code), message);
  return new ApiError(message, code, response.status, body.details);
}

function statusToCode(status: number): ErrorCodeType {
  if (status === 401) return ErrorCode.AUTHENTICATION_FAILED;
  if (status === 402) return ErrorCode.PAYMENT_REQUIRED;
  if (status === 403) return ErrorCode.AUTHORIZATION_FAILED;
  if (status === 404) return ErrorCode.RESOURCE_NOT_FOUND;
  if (status === 429) return ErrorCode.RATE_LIMIT_EXCEEDED;
  if (status === 400) return ErrorCode.VALIDATION_FAILED;
  if (status === 504) return ErrorCode.TIMEOUT_ERROR;
  if (status >= 500) return ErrorCode.INTERNAL_ERROR;
  return ErrorCode.INVALID_REQUEST;
}

// ── Retry with Exponential Backoff ──────────────────────────────────────────

interface RetryOptions {
  maxRetries?: number;
  baseDelayMs?: number;
  maxDelayMs?: number;
  signal?: AbortSignal;
}

/**
 * Retry an async operation with exponential backoff.
 * Only retries on retryable errors (5xx, timeouts, network failures).
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {},
): Promise<T> {
  const { maxRetries = 2, baseDelayMs = 500, maxDelayMs = 5000, signal } = options;

  let lastError: Error = new Error('Retry failed');
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    if (signal?.aborted) throw new ApiError('Request aborted', ErrorCode.ABORT_ERROR, 0);

    try {
      return await fn();
    } catch (err) {
      lastError = err instanceof Error ? err : new Error(String(err));

      // Don't retry non-retryable errors
      if (err instanceof ApiError && !err.retryable) throw err;

      // Don't retry on last attempt
      if (attempt === maxRetries) break;

      // Exponential backoff with jitter
      const delay = Math.min(baseDelayMs * 2 ** attempt + Math.random() * 200, maxDelayMs);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  throw lastError;
}

// ── User-Friendly Error Messages ────────────────────────────────────────────

/**
 * Convert an ApiError code to a user-friendly message.
 * Follows CLAUDE.md copywriting rules: plain language, no jargon.
 */
// Backend cap / paid-feature gate codes that carry a plain-English upsell we
// must show verbatim ("delete one, or upgrade" / "…is a Strategist feature").
// They arrive as the server's `error_code` string, so they don't match the
// ErrorCode enum below — without this they'd flatten to the generic default
// and the conversion prompt (and the recovery instruction) would be lost.
// Kept in step with `UPSELL_CODES` in functions/tests/api/test_paid_gate_status_codes.py,
// which asserts every one of them ships on a 402. The `may ride on a 403` case
// below is now belt-and-braces — the backend standardised on 402 ([BUGS #24]) —
// but a stale deployment of one function must not silently lose the upsell.
// Codes whose server sentence already says what happened and what to do —
// written in plain English on the backend, carried through unchanged.
const PLAIN_REASON_CODES = new Set<string>([ErrorCode.COMPUTE_UNAVAILABLE]);


export function friendlyErrorMessage(error: ApiError): string {
  // Preserve the server's upsell copy for plan caps / paid-feature gates —
  // a 402, or a known cap/entitlement code (which may ride on a 403). The
  // backend already writes these in plain English per the copywriting rules.
  if ((error.status === 402 || UPSELL_CODES.has(error.code as string)) && error.message) {
    return error.message;
  }
  // A compute failure that arrives with its REASON ([[BUGS #369]] item 5) —
  // "took too long, try fewer holdings" — is the sentence the reader needs;
  // flattening it to "Something went wrong" is the defect that entry records.
  if (PLAIN_REASON_CODES.has(error.code as string) && error.message) {
    return error.message;
  }
  switch (error.code) {
    case ErrorCode.AUTHENTICATION_FAILED:
      return 'Please sign in again to continue.';
    case ErrorCode.AUTHORIZATION_FAILED:
      return "You don't have access to this resource.";
    case ErrorCode.RESOURCE_NOT_FOUND:
      return "We couldn't find what you're looking for.";
    case ErrorCode.RATE_LIMIT_EXCEEDED:
      return 'Too many requests. Please wait a moment and try again.';
    case ErrorCode.VALIDATION_FAILED:
      return error.message || 'Please check your input and try again.';
    case ErrorCode.TIMEOUT_ERROR:
      return 'This is taking longer than expected. Please try again.';
    case ErrorCode.NETWORK_ERROR:
      return 'Connection issue. Check your internet and try again.';
    case ErrorCode.EXTERNAL_SERVICE_ERROR:
      return 'A data provider is temporarily unavailable. Please try again shortly.';
    default:
      return 'Something went wrong. Please try again.';
  }
}
